/**
 * Timesheet contract (RBAC area: `time`).
 *
 * A timesheet groups all of a member's time entries for a single ISO work week
 * (Monday-start) and carries the submit/approve lifecycle. Individual time entries
 * are never submitted or approved on their own — the timesheet is the unit of
 * submission and approval. Hour totals are decimals serialized as strings end-to-end
 * (see docs/conventions.md money rules).
 */
export const TIMESHEET_STATUSES = ['draft', 'submitted', 'approved', 'rejected'];
/** Per-approver step status in a tiered (sequential) timesheet approval chain. */
export const TIMESHEET_APPROVAL_STEP_STATUSES = ['pending', 'approved', 'rejected'];
/**
 * Live due state of a not-yet-submitted timesheet relative to the global due date/time:
 * `not_due` (deadline is in a future week), `due_this_week` (deadline is this week and not
 * yet passed) or `overdue` (deadline has passed). Null when no due date is configured or
 * the timesheet is already submitted/approved (see `submissionTiming` instead).
 */
export const TIMESHEET_DUE_STATUSES = ['not_due', 'due_this_week', 'overdue'];
/**
 * Whether a timesheet was submitted before (`on_time`) or after (`past_due`) its due
 * deadline. Captured (and frozen) on the timesheet when it is submitted, so it remains a
 * stable historical fact for lists, forms and reports even if the global due date changes.
 */
export const TIMESHEET_SUBMISSION_TIMINGS = ['on_time', 'past_due'];
/** Statuses in which a timesheet (and the entries it contains) may be edited. */
export const TIMESHEET_EDITABLE_STATUSES = ['draft', 'rejected'];
/**
 * Allowed status transitions. Keys are the current status; values are the set of
 * statuses reachable from it. `approved` is terminal (the ledger engine consumes
 * approved time downstream).
 */
export const TIMESHEET_TRANSITIONS = {
    draft: ['submitted'],
    submitted: ['approved', 'rejected'],
    approved: [],
    rejected: ['submitted', 'draft'],
};
/** True when a timesheet may move from `from` to `to`. */
export function canTransitionTimesheet(from, to) {
    return TIMESHEET_TRANSITIONS[from].includes(to);
}
/** True when a timesheet in `status` may have its entries edited or added/removed. */
export function isTimesheetEditable(status) {
    return TIMESHEET_EDITABLE_STATUSES.includes(status);
}
/**
 * Returns the ISO date (yyyy-mm-dd) of the Monday that starts the work week
 * containing `iso`. Pure UTC date math; input must be a valid ISO date.
 */
export function mondayOf(iso) {
    const date = new Date(`${iso}T00:00:00.000Z`);
    // getUTCDay: 0=Sun..6=Sat. Days since Monday = (day + 6) % 7.
    const daysSinceMonday = (date.getUTCDay() + 6) % 7;
    date.setUTCDate(date.getUTCDate() - daysSinceMonday);
    return date.toISOString().slice(0, 10);
}
//# sourceMappingURL=timesheet.js.map